/**
 * useTabParam — bind a Radix Tabs `value` / `onValueChange` to a URL
 * search param, so back/forward buttons and shareable URLs land on the
 * same active tab (Phase 2 V2-FIX-09 — see PLAN goal 3 + W3.T2(j)).
 *
 * Contract:
 *   const [tab, setTab] = useTabParam(['active', 'pending', 'archived']);
 *   <Tabs value={tab} onValueChange={setTab}>…</Tabs>
 *
 *   - On mount: reads `?tab=…`. If absent or not in `allowed`, returns
 *     `defaultTab` (first allowed value if no override).
 *   - On `setTab(next)`: replaces the search param via `setSearchParams`
 *     with `replace: true` so the back-stack doesn't accumulate one entry
 *     per click. Browser-back still restores the prior tab because the
 *     OUTER navigation that brought the user to the page is still on the
 *     stack (we only collapse intra-page tab thrash).
 *   - The hook never auto-scrolls; React Router's default `setSearchParams`
 *     does not scroll-to-top either, so tab switches stay in place.
 *
 * Returning `(string, fn)` instead of inferring a literal union keeps the
 * hook reusable across pages with different tab vocabularies; consumers
 * cast to their own union when they want exhaustiveness checks.
 */
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UseTabParamOptions {
  /** Search-param key. Defaults to `'tab'`. */
  paramKey?: string;
  /** Override the fallback tab. Defaults to `allowed[0]`. */
  defaultTab?: string;
}

export function useTabParam(
  allowed: readonly string[],
  options: UseTabParamOptions = {},
): [string, (next: string) => void] {
  const { paramKey = 'tab', defaultTab } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const fallback = defaultTab ?? allowed[0] ?? '';
  const param = searchParams.get(paramKey);
  const active = param && allowed.includes(param) ? param : fallback;

  const setTab = React.useCallback(
    (next: string) => {
      // Guard: ignore values outside the allowlist instead of writing
      // garbage to the URL.
      if (!allowed.includes(next)) return;
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set(paramKey, next);
          // FT.D5 / UIDA-B4: reset `?page=` on tab change. Without
          // this the user can be stranded on an empty page — e.g.
          // archived/page=7 → switch to «Активные» → 25*7=175 offset
          // > active rows → empty list with no recourse short of
          // editing the URL by hand. Resetting on every tab pivot
          // is the conservative choice (the `?page` slot is
          // conceptually scoped to the current tab anyway).
          updated.delete('page');
          return updated;
        },
        { replace: true },
      );
    },
    [allowed, paramKey, setSearchParams],
  );

  return [active, setTab];
}
